import { DateFormatter } from '@repo/toolkit/date'
import { DateRangeInput } from '@repo/ui/date-range-input'
import Input from '@repo/ui/input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import RadioInput from '@repo/ui/radio-input'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getCountries } from '../../../api/countries/get-countries'
import { getFormulas } from '../../../api/formula/get-formulas'
import { getPeriods } from '../../../api/periods/get-periods'
import { getRads } from '../../../api/rads/get-rads'
import { getTransportMeans } from '../../../api/transport-means/get-transport-means'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  AccomodationEnum,
  accomodationEnumMapping
} from '../../../lib/types/enums/accomodation-type/accomodation-type'
import {
  LanguageEnum,
  languageEnumMapping
} from '../../../lib/types/enums/languages/language.enum'
import {
  AcademicZoneEnum,
  academicZoneEnumMapping
} from '../../../lib/types/enums/staff/academic-zone.enum'
import {
  StaffPositionEnum,
  staffPositionEnumMapping
} from '../../../lib/types/enums/staff/staff-position.enum'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../../lib/types/enums/staff/staff-qualifications.enum'
import {
  DiplomaEnum,
  diplomaEnumMapping
} from '../../../lib/types/enums/staff-application/diploma.enum'
import { StaffFilters } from '../../../lib/types/models/staff/staff'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'

interface Props {
  filter?: StaffFilters
  onFilterChange: (filter: StaffFilters) => void
}

export function StaffsFilter({ filter, onFilterChange }: Props) {
  const { t } = useTranslate()
  function setFilter<K extends keyof StaffFilters>(key: K, value: StaffFilters[K]) {
    onFilterChange({ ...filter, [key]: value })
  }
  const languageMapping = languageEnumMapping()

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  const { data: transportMeans } = useQuery({
    queryKey: ['transport-means'],
    queryFn: () => getTransportMeans()
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: rads } = useQuery({
    queryKey: ['rads'],
    queryFn: () => getRads()
  })

  return (
    <div className={'flex flex-col gap-3'}>
      <div className="flex flex-row gap-5">
        <RadioInput
          checked={filter?.active === undefined}
          label={'Les deux'}
          onCheck={() => {
            onFilterChange({ ...filter, active: undefined })
          }}
        />
        <RadioInput
          checked={filter?.active === true}
          label={'Actif'}
          onCheck={() => {
            onFilterChange({ ...filter, active: true })
          }}
        />
        <RadioInput
          checked={filter?.active === false}
          label={'Inactif'}
          onCheck={() => {
            onFilterChange({ ...filter, active: false })
          }}
        />
      </div>

      <div className="flex flex-row gap-5">
        <RadioInput
          checked={filter?.IDF === undefined}
          label={'Les deux'}
          onCheck={() => {
            onFilterChange({ ...filter, IDF: undefined })
          }}
        />
        <RadioInput
          checked={filter?.IDF === true}
          label={'IDF'}
          onCheck={() => {
            onFilterChange({ ...filter, IDF: true })
          }}
        />
        <RadioInput
          checked={filter?.IDF === false}
          label={'Non IDF'}
          onCheck={() => {
            onFilterChange({ ...filter, IDF: false })
          }}
        />
      </div>

      <div className="flex flex-col gap-2.5">
        <Typography variant="tag">Statut de l'espace encadrant</Typography>
        <div className="flex flex-row gap-5">
          <RadioInput
            checked={filter?.accountActive === undefined}
            label={'Les deux'}
            onCheck={() => onFilterChange({ ...filter, accountActive: undefined })}
          />
          <RadioInput
            checked={filter?.accountActive === true}
            label={'Actif'}
            onCheck={() => onFilterChange({ ...filter, accountActive: true })}
          />
          <RadioInput
            checked={filter?.accountActive === false}
            label={'Désactivé'}
            onCheck={() => onFilterChange({ ...filter, accountActive: false })}
          />
        </div>
      </div>

      <MultiSelectInput
        label={'Poste'}
        searchable
        variant={'primary'}
        options={Object.values(StaffPositionEnum).map((position) => ({
          label: t(staffPositionEnumMapping[position]),
          value: position
        }))}
        onChange={(position) => setFilter('position', position)}
        selectedOptions={filter?.position?.map((position) => ({
          label: t(staffPositionEnumMapping[position]),
          value: position
        }))}
      />

      <MultiSelectInput
        label={'Qualifications'}
        variant={'primary'}
        searchable
        options={Object.values(StaffQualificationEnum).map((qualification) => ({
          label: t(staffQualificationEnumMapping[qualification].label),
          value: qualification
        }))}
        onChange={(qualifications) => setFilter('qualifications', qualifications)}
        selectedOptions={filter?.qualifications?.map((qualification) => ({
          label: t(staffQualificationEnumMapping[qualification].label),
          value: qualification
        }))}
      />

      <MultiSelectInput
        label={'Langue'}
        variant={'primary'}
        searchable
        options={Object.values(LanguageEnum).map((language) => ({
          label: languageMapping[language].label,
          value: language
        }))}
        onChange={(languages) => setFilter('languages', languages)}
        selectedOptions={filter?.languages?.map((language) => ({
          label: languageMapping[language].label,
          value: language
        }))}
      />

      <MultiSelectInput
        label={'Diplôme'}
        variant={'primary'}
        searchable
        options={Object.values(DiplomaEnum).map((diploma) => ({
          label: t(diplomaEnumMapping[diploma]),
          value: diploma
        }))}
        onChange={(diplomas) => setFilter('diplomas', diplomas)}
        selectedOptions={filter?.diplomas?.map((diploma) => ({
          label: t(diplomaEnumMapping[diploma]),
          value: diploma
        }))}
      />

      <SelectInput
        label={'Zone Académique'}
        options={Object.values(AcademicZoneEnum).map((academicZone) => ({
          label: academicZoneEnumMapping[academicZone],
          value: academicZone
        }))}
        onChange={(academicZone) => setFilter('academicZone', academicZone)}
        selectedOption={
          filter?.academicZone === undefined
            ? undefined
            : { label: academicZoneEnumMapping[filter.academicZone], value: filter.academicZone }
        }
      />

      <div className={'flex flex-row gap-2'}>
        <div className={'flex flex-col gap-2 w-full'}>
          <Input
            type={'number'}
            label={'Âge min'}
            value={filter?.minAge ? +filter.minAge : undefined}
            errorMessage={
              filter?.minAge && filter.minAge < 0 ? "L'âge doit être supérieur à zéro" : undefined
            }
            onChange={(e) =>
              setFilter('minAge', +e.target.value === 0 ? undefined : +e.target.value)
            }
          />
        </div>

        <div className={'flex flex-col gap-2 w-full'}>
          <Input
            type={'number'}
            label={'Âge max'}
            value={filter?.maxAge ? +filter.maxAge : undefined}
            errorMessage={
              (filter?.maxAge && filter.maxAge < 0) ||
              (filter?.maxAge && filter.minAge && filter.maxAge < filter.minAge)
                ? "L'âge maximum doit être supérieur à zéro et l'âge minimum"
                : undefined
            }
            onChange={(e) =>
              setFilter('maxAge', +e.target.value === 0 ? undefined : +e.target.value)
            }
          />
        </div>
      </div>
      <div className={'flex flex-col gap-3'}>
        <DateRangeInput
          label={'Disponible'}
          value={
            filter?.availability?.startDate && filter?.availability?.endDate
              ? {
                  from: DateFormatter.toDate(filter.availability.startDate),
                  to: DateFormatter.toDate(filter.availability.endDate)
                }
              : undefined
          }
          onClearDate={() => onFilterChange({ ...filter, availability: undefined })}
          onDateRangeChange={(dateRange) => {
            if (dateRange?.from && dateRange?.to)
              onFilterChange({
                ...filter,
                availability: {
                  startDate: DateFormatter.toDateFormatted(dateRange?.from),
                  endDate: DateFormatter.toDateFormatted(dateRange?.to)
                }
              })
          }}
        />

        <DateRangeInput
          label={'Départ'}
          value={
            filter?.departure?.startDate && filter?.departure?.endDate
              ? {
                  from: DateFormatter.toDate(filter.departure.startDate),
                  to: DateFormatter.toDate(filter.departure.endDate)
                }
              : undefined
          }
          onClearDate={() => onFilterChange({ ...filter, departure: undefined })}
          onDateRangeChange={(dateRange) => {
            if (dateRange?.from && dateRange?.to)
              onFilterChange({
                ...filter,
                departure: {
                  startDate: DateFormatter.toDateFormatted(dateRange?.from),
                  endDate: DateFormatter.toDateFormatted(dateRange?.to)
                }
              })
          }}
        />

        <DateRangeInput
          label={'Retour'}
          value={
            filter?.arrival?.startDate && filter?.arrival?.endDate
              ? {
                  from: DateFormatter.toDate(filter.arrival.startDate),
                  to: DateFormatter.toDate(filter.arrival.endDate)
                }
              : undefined
          }
          onClearDate={() => onFilterChange({ ...filter, arrival: undefined })}
          onDateRangeChange={(dateRange) => {
            if (dateRange?.from && dateRange?.to)
              onFilterChange({
                ...filter,
                arrival: {
                  startDate: DateFormatter.toDateFormatted(dateRange?.from),
                  endDate: DateFormatter.toDateFormatted(dateRange?.to)
                }
              })
          }}
        />

        <DateRangeInput
          label={'Dates de séjour'}
          value={
            filter?.stay?.startDate && filter?.stay?.endDate
              ? {
                  from: DateFormatter.toDate(filter.stay.startDate),
                  to: DateFormatter.toDate(filter.stay.endDate)
                }
              : undefined
          }
          onClearDate={() => onFilterChange({ ...filter, stay: undefined })}
          onDateRangeChange={(dateRange) => {
            if (dateRange?.from && dateRange?.to)
              onFilterChange({
                ...filter,
                stay: {
                  startDate: DateFormatter.toDateFormatted(dateRange?.from),
                  endDate: DateFormatter.toDateFormatted(dateRange?.to)
                }
              })
          }}
        />
      </div>

      <MultiSelectInput
        searchable
        label={'Périodes'}
        variant={'warning'}
        options={periods?.data?.map((period) => ({ label: period.name, value: period.id }))}
        onChange={(periodIds) => setFilter('periodIds', periodIds)}
        selectedOptions={filter?.periodIds?.map((periodId) => ({
          label: periods?.data?.find((p) => p.id === periodId)?.name || '',
          value: periodId
        }))}
      />

      <MultiSelectInput
        searchable
        label={'Pays de séjour'}
        variant={'primary'}
        options={countries?.data?.map((country) => ({ label: country.name, value: country.id }))}
        onChange={(countryIds) => setFilter('countriesWishList', countryIds)}
        selectedOptions={filter?.countriesWishList?.map((countryId) => ({
          label: countries?.data?.find((p) => p.id === countryId)?.name || '',
          value: countryId
        }))}
      />

      <MultiSelectInput
        label={'Formule de séjour'}
        variant={'primary'}
        searchable
        options={formulas?.data?.map((formula) => ({
          label: getTranslatedName(formula),
          value: formula.id
        }))}
        onChange={(formulaIds) => setFilter('formulaIds', formulaIds)}
        selectedOptions={filter?.formulaIds?.map((formulaId) => ({
          label: getTranslatedName(formulas?.data?.find((f) => f.id === formulaId)),
          value: formulaId
        }))}
      />

      <MultiSelectInput
        label={'Hébergement des séjours'}
        variant={'primary'}
        searchable
        options={Object.values(AccomodationEnum).map((option) => ({
          label: accomodationEnumMapping[option],
          value: option
        }))}
        selectedOptions={filter?.accomodations?.map((accommodation) => ({
          label: accomodationEnumMapping[accommodation],
          value: accommodation
        }))}
        onChange={(accomodations) => setFilter('accomodations', accomodations)}
      />

      <MultiSelectInput
        searchable
        label={'Moyen de transport du séjour'}
        variant={'primary'}
        options={transportMeans?.data?.map((transportMean) => ({
          label: getTranslatedName(transportMean),
          value: transportMean.id
        }))}
        onChange={(transportMeansIds) => setFilter('transportMeansIds', transportMeansIds)}
        selectedOptions={filter?.transportMeansIds?.map((transportMeansId) => ({
          label: getTranslatedName(transportMeans?.data?.find((t) => t.id === transportMeansId)),
          value: transportMeansId
        }))}
      />

      <MultiSelectInput
        label={'Type de pli'}
        variant={'primary'}
        options={rads?.data?.map((rad) => ({ label: rad.title, value: rad.id }))}
        onChange={(radIds) => setFilter('radIds', radIds)}
        selectedOptions={filter?.radIds?.map((radId) => ({
          label: rads?.data?.find((t) => t.id === radId)?.title || '',
          value: radId
        }))}
      />
    </div>
  )
}
